/**
 * Store recent transaction hashes for display and explorer links.
 * Persisted in localStorage; keyed by address+network for multi-wallet support.
 */

const STORAGE_KEY = 'boing-express-tx-history';
const MAX_ENTRIES = 50;

export interface TxHistoryEntry {
  txHash: string;
  type: 'send' | 'bond' | 'unbond' | 'claim_unbond' | 'token_send' | 'nft_send' | 'contract_call' | 'batch'
    | 'validator_register' | 'validator_deregister' | 'validator_unjail';
  /** Short human summary, e.g. "10 BOING → 7f3a…9c21" (optional; older entries have none). */
  summary?: string;
  /** Batch entries: one line per step, in order (max 16). */
  steps?: string[];
  networkId: string;
  addressHint: string; // first 8 + last 8 of address
  timestamp: number;
}

function getStorageKey(addressHint: string, networkId: string): string {
  return `${STORAGE_KEY}:${addressHint}:${networkId}`;
}

function isValidTxHash(txHash: unknown): txHash is string {
  return typeof txHash === 'string' && txHash.length > 0;
}

export function addTxHistory(
  addressHint: string,
  networkId: string,
  txHash: string,
  type: TxHistoryEntry['type'],
  summary?: string,
  steps?: string[]
): void {
  if (!isValidTxHash(txHash)) return;
  try {
    const key = getStorageKey(addressHint, networkId);
    const raw = localStorage.getItem(key);
    const entries: TxHistoryEntry[] = raw ? JSON.parse(raw) : [];
    entries.unshift({
      txHash,
      type,
      networkId,
      addressHint,
      timestamp: Date.now(),
      ...(summary ? { summary: summary.slice(0, 120) } : {}),
      ...(steps && steps.length ? { steps: steps.slice(0, 16).map((x) => x.slice(0, 120)) } : {}),
    });
    const trimmed = entries.slice(0, MAX_ENTRIES);
    localStorage.setItem(key, JSON.stringify(trimmed));
  } catch {
    // Ignore storage errors
  }
}

export function getTxHistory(addressHint: string, networkId: string): TxHistoryEntry[] {
  try {
    const key = getStorageKey(addressHint, networkId);
    const raw = localStorage.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is TxHistoryEntry =>
        entry != null &&
        typeof entry === 'object' &&
        isValidTxHash((entry as TxHistoryEntry).txHash) &&
        (entry as TxHistoryEntry).type != null
    );
  } catch {
    return [];
  }
}
